/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Every static file this application owns, named once.
 *
 * Item 12 (Session 361). These files used to be published under `/images/`,
 * a path the older ASP.NET site also answers for. That made the folder a
 * shared namespace rather than a clean hand-off: the old site answered for
 * the names it happened to hold, this deployment answered for names it held
 * and the old site did not, and names neither held fell through to a redirect
 * to the old site's front page or to the single-page fallback. On the live
 * site that broke the header and footer logo, the shopping-cart icon and the
 * icon in the browser tab — all of which returned a web page, or a redirect,
 * instead of an image.
 *
 * Three things about the destination are deliberate:
 *
 *  - `/assets/` is served by this deployment, and a request for a file that
 *    does not exist under it is answered here rather than redirected to the
 *    old site. That is the property `/images/` did not have.
 *  - The files sit in `/assets/img/`, not directly in `/assets/`. The build
 *    writes its own hashed output into `/assets/`, and mixing hand-named files
 *    into the same folder invites a collision that would appear only at build
 *    time and only sometimes. One sub-folder removes the question.
 *  - Every address is written here and nowhere else. If these files ever move
 *    again it is one edit, not a search across a dozen components — which is
 *    the whole reason this module exists.
 *
 * These are runtime addresses, not module imports: the files live in `public/`
 * and are copied to the build root unhashed, so they are referenced by path.
 *
 * WARNING: `/assets/` must be served from this deployment on every
 * environment, and the old site's catch-all redirect must not capture requests
 * for files this deployment owns. That change belongs to the operations team
 * and must be in place on an environment BEFORE this code is deployed to it —
 * see the handoff. FALLBACK_IMAGE below is the address every failed product
 * image falls back to, so deploying the code first turns one broken image into
 * a page of broken images.
 */

/** Root of the static files this application owns. */
export const STATIC_ASSET_BASE = "/assets/img";

export const STATIC_ASSETS = {
  /** Site logo, raster. Social-sharing and structured-data tags use this. */
  logoPng: `${STATIC_ASSET_BASE}/deskshare_logo.png`,
  /** Site logo, vector. Header and footer. */
  logoSvg: `${STATIC_ASSET_BASE}/deskshare_logo.svg`,
  /** Progressive-web-app icon, also used as the Apple touch icon. */
  logo192: `${STATIC_ASSET_BASE}/deskshare_logo192.png`,
  /** Shopping-cart mark on the two purchase pages. */
  cartIcon: `${STATIC_ASSET_BASE}/cart-icon.svg`,
  /** Manual mark in the tutorials and manuals list. */
  manualBookIcon: `${STATIC_ASSET_BASE}/manual-book-icon.svg`,
  /** FAQ mark, for products whose help column links to /faq/... */
  faqIcon: `${STATIC_ASSET_BASE}/faq-icon.svg`,
  /** Money-back guarantee badge on the two purchase pages. */
  guaranteed: `${STATIC_ASSET_BASE}/guaranteed.png`,
  /** Browser-tab icon. Requested on every page load. */
  favicon32: `${STATIC_ASSET_BASE}/deskshare_icon32.ico`,
} as const;

/**
 * Base path for the localised hero artwork on the home page.
 *
 * The filenames are built from a language suffix at runtime
 * (`Hero_En.webp`, `Hero_Tablet_De.webp`, and so on), so this is the one
 * address that is a prefix rather than a whole file.
 */
export const HERO_IMAGE_BASE_PATH = `${STATIC_ASSET_BASE}/`;

/**
 * Shown in place of any image that fails to load, and in the loading splash.
 *
 * Deliberately the same constant the rest of the site uses rather than a
 * second copy: if this address is ever wrong, every failed image on the site
 * becomes a second failed image.
 */
export const FALLBACK_IMAGE = STATIC_ASSETS.logoPng;
